/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Autocomplete.
 *
 * The input half matches the 48px / 40px control system. MUI styles the
 * Autocomplete's OutlinedInput from the Autocomplete root at higher
 * specificity than the MuiOutlinedInput overrides, so the same selectors are
 * used here; never the `padding` shorthand, which would wipe the room MUI
 * reserves for the clear and popup icons.
 *
 * The popup half matches menu/index.js and menu/menuItem.js so a Select and
 * an Autocomplete read as the same control. The chrome lives on the paper,
 * not the popper: the popper is the element cssZoomOverlays repositions.
 */

import boxShadows from "assets/theme-dark/base/boxShadows";
import typography from "assets/theme-dark/base/typography";
import colors from "assets/theme-dark/base/colors";
import borders from "assets/theme-dark/base/borders";
import pxToRem from "assets/theme-dark/functions/pxToRem";

const { md } = boxShadows;
const { size } = typography;
const { brand, text, outline, surface, background } = colors;
const { borderRadius } = borders;

const autocomplete = {
  styleOverrides: {
    root: {
      "& .MuiOutlinedInput-root": {
        minHeight: pxToRem(48),
        paddingTop: pxToRem(4),
        paddingBottom: pxToRem(4),
        paddingLeft: pxToRem(10),
        "& .MuiAutocomplete-input": {
          padding: `0 ${pxToRem(4)}`,
          height: "auto",
          alignSelf: "stretch",
          minWidth: 30,
        },
        "& .MuiAutocomplete-endAdornment": {
          right: pxToRem(9),
        },
      },
      "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
        minHeight: pxToRem(40),
        paddingTop: pxToRem(2),
        paddingBottom: pxToRem(2),
        paddingLeft: pxToRem(8),
        "& .MuiAutocomplete-input": {
          padding: `0 ${pxToRem(4)}`,
          fontSize: pxToRem(13),
        },
      },
      "& .MuiAutocomplete-tag": {
        margin: pxToRem(2),
      },
    },

    popupIndicator: {
      color: outline.main,
      padding: pxToRem(2),
    },
    clearIndicator: {
      color: outline.main,
      padding: pxToRem(2),
    },

    popper: {},

    paper: {
      padding: pxToRem(6),
      marginTop: pxToRem(4),
      borderRadius: borderRadius.md,
      border: `1px solid ${outline.variant}`,
      boxShadow: md,
      backgroundColor: background.paper,
      color: text.main,
      fontSize: size.sm,
      textAlign: "left",
    },

    listbox: {
      padding: 0,
      maxHeight: "40vh",
    },

    option: {
      minHeight: `${pxToRem(36)} !important`,
      padding: `${pxToRem(8)} ${pxToRem(12)} !important`,
      borderRadius: borderRadius.sm,
      fontSize: size.sm,
      color: text.main,
      transition: "background-color 120ms ease, color 120ms ease",
      "&.Mui-focused, &:hover": {
        backgroundColor: surface.container,
      },
      "&[aria-selected='true'], &[aria-selected='true'].Mui-focused, &[aria-selected='true']:hover": {
        backgroundColor: brand.primaryContainer,
        color: brand.primary,
        fontWeight: 600,
      },
      "&.Mui-focusVisible": {
        boxShadow: `inset 0 0 0 1px ${brand.primary}`,
      },
    },

    groupLabel: {
      color: text.focus,
      fontSize: size.xs,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: ".04em",
      lineHeight: pxToRem(28),
      backgroundColor: background.paper,
    },

    noOptions: {
      fontSize: size.sm,
      color: text.focus,
      padding: `${pxToRem(10)} ${pxToRem(12)}`,
    },
    loading: {
      fontSize: size.sm,
      color: text.focus,
      padding: `${pxToRem(10)} ${pxToRem(12)}`,
    },

    tag: {
      height: pxToRem(24),
      margin: pxToRem(2),
      borderRadius: borderRadius.sm,
      backgroundColor: brand.primaryContainer,
      color: brand.onPrimaryContainer,
      fontSize: pxToRem(12),
      fontWeight: 600,
      "& .MuiChip-label": {
        padding: `0 ${pxToRem(8)}`,
        lineHeight: 1.2,
      },
      "& .MuiChip-deleteIcon": {
        color: brand.primary,
        fontSize: pxToRem(16),
        marginRight: pxToRem(2),
        "&:hover": {
          color: brand.onPrimaryContainer,
        },
      },
    },
  },
};

export default autocomplete;
